// =============================================================================
// ui/state/useGoals.ts — the goals card's own read.
// -----------------------------------------------------------------------------
// A HOOK, NOT A PROVIDER, for the same reason `usePropUniverse` is: exactly one
// surface needs it, and a provider would make every user pay the read to open
// any tab.
//
// ── 🚨 TWO READS, AND THE SECOND IS SIZED BY THE FIRST ──
//
// Goals carry their own periods, so the trades this card needs are whatever the
// WIDEST goal reaches back to — today for a daily target, January for a yearly
// one, the epoch for an all-time one. That is knowable only after the goals
// themselves have loaded, so the trade read is issued second, bounded by
// `earliestGoalStart`.
//
// ⚠ A user with only daily and weekly goals therefore pays for a week, not a
// year. That is the whole reason this is not `usePropUniverse`: the Prop Report
// genuinely needs the whole book and mints a `WholeBook` receipt to prove it
// (D79), whereas a goal's window is bounded and known. Reaching for the
// whole-book read because it was already there would tax every dashboard load
// with a read no goal on the screen asked for.
//
// ⚠ AND THE BOUND IS PADDED. `loadTradeMetrics` filters on `date`, the indexed
// column, while a goal window is compared against the EFFECTIVE (session) date.
// A trade dated 2025-12-31 can carry a 2026-01-01 session date and belong to the
// yearly goal. Without the pad it is silently outside the query. Same fix, same
// reason, as `loadTradesInRange`.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useSupabase } from './supabase';
import { loadGoals } from '../../io/sync/goals';
import { SESSION_PAD_DAYS } from '../../io/sync/load';
import { loadGoalTrades } from '../../io/sync/goalTrades';
import { loadJournalRange } from '../../io/sync/journal';
import {
  earliestGoalStart, type Goal, type GoalJournalDay, type GoalTrade,
} from '../../selectors/goals';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type GoalsPhase = 'loading' | 'ready' | 'failed';

export interface GoalsState {
  phase: GoalsPhase;
  /** ⚠ NULL until BOTH reads have landed — see below. */
  goals: readonly Goal[] | null;
  trades: readonly GoalTrade[] | null;
  /**
   * Journal days, for the two templates measured over DAYS. Null on a failed
   * read alongside the trades — an empty list would render a streak of 0, which
   * is a claim about the trader rather than an admission about the read.
   */
  journal: readonly GoalJournalDay[] | null;
  /** The WALL-CLOCK day the windows are anchored to. Not the session anchor. */
  todayIso: string;
  error: string | null;
}

/** Today, by the wall clock. See `domain/goals.ts` note 3 for why not the session day. */
function wallClockToday(now: Date): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const shift = (isoDate: string, days: number): string => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * @param now injectable so a test can pin the day. The card's whole meaning is
 *   "this period", and a test that reads the machine's clock asserts a different
 *   window every day it runs.
 */
export function useGoals(now: Date = new Date()): GoalsState {
  const { sb } = useSupabase();

  const [phase, setPhase] = useState<GoalsPhase>('loading');
  const [goals, setGoals] = useState<readonly Goal[] | null>(null);
  const [trades, setTrades] = useState<readonly GoalTrade[] | null>(null);
  const [journal, setJournal] = useState<readonly GoalJournalDay[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ⚠ Keyed on the DAY, not the Date object. A fresh `new Date()` on every
  // render would re-fire the read continuously.
  const todayIso = useMemo(() => wallClockToday(now), [now]);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      try {
        const g = await loadGoals(sb);
        if (cancelled) return;

        const earliest = earliestGoalStart(g, todayIso);
        // ⚠ NO GOALS MEANS NO READ AT ALL — not an unbounded one. A null bound
        // reads the entire book, which is the largest read in the client issued
        // on behalf of a card that has nothing to draw.
        const since = earliest === null ? null : shift(earliest, -SESSION_PAD_DAYS);

        /**
         * ── 🚨 TWO READS NOW, AND THE SECOND IS NOT OPTIONAL (#48) ──
         *
         * Three of the four templates are measured over JOURNAL DAYS or need
         * fields `loadTradeMetrics` does not carry, which is exactly why the
         * card said "not tracked here yet" for all three. `loadGoalTrades` adds
         * `annotated`, `booleans` and `mistake_tags`; the journal read supplies
         * the day-level flags that `streak` and `boolean-adherence` over days
         * count.
         *
         * ⚠ IN PARALLEL. They do not depend on each other and this card is on
         * the landing screen; sequencing them would double its time to first
         * paint for no reason.
         *
         * ⚠ THE JOURNAL READ IS **UNBOUNDED BELOW**, deliberately. A streak is a
         * trailing run backwards from the most recent entry, so clipping it to
         * the goal window would reset every streak on the 1st of the month —
         * the opposite of what a streak measures. Bounded ABOVE at today so a
         * future-dated entry cannot extend a run that has not happened.
         */
        const [rows, days] = since === null
          ? [[], []]
          : await Promise.all([
              loadGoalTrades(sb, since),
              loadJournalRange(sb, '1970-01-01', todayIso),
            ]);
        if (cancelled) return;

        // ⚠ Projected FIELD BY FIELD (D78) rather than handed over whole. The
        // row is wider than the goal arithmetic's input, and passing the record
        // is what let an earlier "it gets the full object" control pass against
        // code that was already narrowing.
        setGoals(g);
        setTrades(rows.map((t) => ({
          accountId: t.accountId,
          date: t.date,
          sessionDate: t.sessionDate,
          netPnl: t.netPnl,
          annotated: t.annotated,
          booleans: t.booleans,
          mistakeTags: t.mistakeTags,
        })));
        setJournal(days.map((j) => ({ date: j.date, booleans: j.booleans })));
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ Both stay NULL. An empty goals array is "you have set no targets",
        // which is a claim — and the wrong one to make on a failed read.
        setError(reportFailure('goals', e));
        setPhase('failed');
        // ⚠ The journal stays NULL alongside the trades. An empty day list is
        // "you have journalled nothing", which would render a streak of 0 — a
        // claim about the trader on the back of a failed read.
      }
    })();

    return () => { cancelled = true; };
  }, [sb, todayIso]);

  return useMemo(
    () => ({ phase, goals, trades, journal, todayIso, error }),
    [phase, goals, trades, journal, todayIso, error],
  );
}
